// 個人化等第制成績設定整頁（instruction-personalization-gpa-score-setting＝legacy
// instruction/personalizationGPASetting.jsp＋personalizationGPATab.jsp，
// /instruction/personalizationGPA/scoreSetting，display 頁型，非選單頁）。RF-147。
// 掛在 PageBehavior.complexBody（DisplayPage 已補早退）。
//
// legacy 流程（行號親驗 2026-10-18，基準＝ePortfolio_work/ePortfolio-tsgh/.../instruction/）：
//   - personalizationGPASetting.jsp:91-104  7 個頁籤 A++／A+／A／A-／B／C／D（scoreTag 1～7），
//     標籤尾端 check_circle 勾號初始 display:none。
//   - personalizationGPASetting.jsp:16-95   initTab：依 choiceList1～7（server 存的已選 fieldOptionId）
//     勾選 radio；該等第有已選＝顯示頁籤勾號。
//   - personalizationGPATab.jsp:134-153     每頁籤 fieldList 逐題一列：左 label（titleStyle，寬 40%）、
//     右單選（name＝ChoiceType|版本欄位ID|scoreTag）。
//   - personalizationGPATab.jsp:157-159     「儲存此頁籤設定」鈕（btn-add，onclick=submitForm(scoreTag)）。
//   - personalizationGPATab.jsp:55-72       saveData：先清除 text-danger；checked radio 數 ≠ 13 →
//     未填題目標題加 text-danger＋showAlertDialog 並結束。
//   - personalizationGPATab.jsp:74-96       選滿 13 題 → POST instruction/personalizationGPA/save
//     （JSON 陣列 {fieldOptionId, fieldTypeId, score}）；status=success → showInfoDialog(resp.desc)，
//     關閉後更新 choiceListN 並重跑 initTab（該頁籤出現勾號）；否則 showAlertDialog(resp.desc)。
//
// 雛形策略：存檔＝update（dataEffect；endpoint-map 對 POST /instruction/personalizationGPA/save 查無
// （路徑末段 save 無 Ajax 後綴，全域規則不逐頁例外），列於 docs/endpoint-map-undecided.json）；
// 13 題為通用 mock（src/mocks/personalization-gpa.ts）；成功訊息 desc 由 server 回傳，Controller 不在
// src/ePortfolio，文字【雛形值待實機校準】；警告文字逐字取自 personalizationGPATab.jsp:69。
// 存檔 payload 同 legacy 格式（formData 陣列）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { type ReactElement, useEffect, useState } from 'react';
import { Button } from '@/components/base/Button';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { MockDataError, useDataSource } from '@/mocks';
import { GPA_FIELDS, GPA_REQUIRED_COUNT, GPA_SCORES } from '@/mocks/personalization-gpa';

/** 訊息字面值。 */
const TEXT_INCOMPLETE = '尚有題目未完成設定，請完成設定後再存檔。'; // personalizationGPATab.jsp:69
const TEXT_SAVE_SUCCESS = '更新資料成功!'; // server resp.desc（Controller 不在 src；【雛形值待實機校準】）
const TEXT_SAVE_ERROR = '更新資料失敗!';

/** scoreTag → 該頁籤的選擇（題目 fieldTypeId → fieldOptionId）。 */
type Selections = Readonly<Record<number, Readonly<Record<number, number>>>>;

export function PersonalizationGPASettingBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  const [activeTag, setActiveTag] = useState(1);
  // 頁面上目前選取（radio checked）。
  const [selections, setSelections] = useState<Selections>({});
  // choiceList1～7：已儲存的選擇；非空＝頁籤顯示勾號（initTab :20-27）。
  const [saved, setSaved] = useState<Readonly<Record<number, readonly number[]>>>({});
  // saveData :59-60 加上 text-danger 的題目（fieldTypeId）；下次存檔前先清除。
  const [warned, setWarned] = useState<Readonly<Record<number, readonly number[]>>>({});
  const [busy, setBusy] = useState(false);
  const [anchorId, setAnchorId] = useState('');

  // pageId 一次掛載內不變（ComplexFormPage 同款註記）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 見上
  useEffect(() => {
    void (async () => {
      try {
        // 錨點列（resource 最後一列；設定存列上＝B1-2 先例）。
        const anchor = await dataSource.list(pageId, { page: 1, pageSize: 12 });
        setAnchorId(String(anchor.rows[anchor.rows.length - 1]?.id ?? ''));
      } catch {
        setAnchorId('');
      }
    })();
  }, [pageId]);

  const choose = (scoreTag: number, fieldTypeId: number, fieldOptionId: number) => {
    setSelections((current) => ({
      ...current,
      [scoreTag]: { ...(current[scoreTag] ?? {}), [fieldTypeId]: fieldOptionId },
    }));
  };

  /** submitForm → saveData（personalizationGPATab.jsp:55-96）。 */
  const saveTab = (scoreTag: number, score: string) => {
    void (async () => {
      const chosen = selections[scoreTag] ?? {};
      const answered = GPA_FIELDS.filter((field) => chosen[field.fieldTypeId] !== undefined);
      // :57-72 先清 text-danger；未選滿 13 題 → 標未填題目＋警告。
      setWarned((current) => ({ ...current, [scoreTag]: [] }));
      if (answered.length !== GPA_REQUIRED_COUNT) {
        setWarned((current) => ({
          ...current,
          [scoreTag]: GPA_FIELDS.filter((field) => chosen[field.fieldTypeId] === undefined).map(
            (field) => field.fieldTypeId,
          ),
        }));
        await dialogs.alert(TEXT_INCOMPLETE);
        return;
      }
      if (anchorId === '') {
        await dialogs.alert(TEXT_SAVE_ERROR);
        return;
      }
      const formData = answered.map((field) => ({
        fieldOptionId: chosen[field.fieldTypeId] as number,
        fieldTypeId: field.fieldTypeId,
        score,
      }));
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.update(pageId, anchorId, { datas: formData }));
        // 送出完成即收起處理中遮罩，成功訊息才點得到（遮罩 z-index 高於對話框）。
        setBusy(false);
        await dialogs.info(TEXT_SAVE_SUCCESS);
        // :88-91 關閉訊息後 window.parent[choiceListN]=temp；initTab() → 頁籤勾號。
        setSaved((current) => ({
          ...current,
          [scoreTag]: formData.map((item) => item.fieldOptionId),
        }));
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_SAVE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div data-ui-id={pageId}>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
      <ContentPanel uiId={`${pageId}-panel`}>
        {/* 頁籤列（jsp:91-104 ul.nav-tabs，active 底色 #FFD306）。 */}
        <div
          data-ui-id={`${pageId}-tabs`}
          className="m-0 flex min-h-[48px] border-brand border-t-[3px] p-0"
          role="tablist"
        >
          {GPA_SCORES.map(({ scoreTag, score }) => {
            const active = scoreTag === activeTag;
            const done = (saved[scoreTag]?.length ?? 0) > 0;
            return (
              <button
                key={scoreTag}
                type="button"
                role="tab"
                data-ui-id={`${pageId}-tab-${scoreTag}`}
                aria-selected={active}
                className={`-mb-px cursor-pointer px-[15px] py-[10px] ${
                  active ? 'bg-[#FFD306] text-white' : 'bg-transparent text-ink hover:text-brand'
                }`}
                onClick={() => setActiveTag(scoreTag)}
              >
                {score}&nbsp;
                <span
                  data-ui-id={`${pageId}-tab-${scoreTag}-done`}
                  className={`material-icons ${active ? 'text-white' : 'text-[#FFD306]'}`}
                  style={{ display: done ? undefined : 'none' }}
                >
                  check_circle
                </span>
              </button>
            );
          })}
        </div>

        {/* 7 個頁籤內容（jsp:112-160；非作用中以 hidden 保留 DOM，同 bootstrap tab-pane）。 */}
        {GPA_SCORES.map(({ scoreTag, score }) => (
          <div
            key={scoreTag}
            data-ui-id={`${pageId}-pane-${scoreTag}`}
            hidden={scoreTag !== activeTag}
            className="bg-surface/70 px-5 py-4"
          >
            <div data-ui-id={`${pageId}-pane-${scoreTag}-fields`}>
              {GPA_FIELDS.map((field) => (
                <div
                  key={field.fieldTypeId}
                  data-ui-id={`${pageId}-pane-${scoreTag}-field-${field.fieldTypeId}`}
                  className="mb-2 flex flex-wrap items-center"
                >
                  <span
                    data-ui-id={`${pageId}-pane-${scoreTag}-field-${field.fieldTypeId}-title`}
                    className={`inline-block w-2/5 align-middle ${
                      warned[scoreTag]?.includes(field.fieldTypeId) ? 'text-danger' : ''
                    }`}
                  >
                    {field.title}
                  </span>
                  <span className="inline-block align-middle">
                    {field.items.map((option, optionIndex) => {
                      const radioId = `${pageId}-pane-${scoreTag}-field-${field.fieldTypeId}-opt-${optionIndex}`;
                      return (
                        <span key={option.fieldOptionId} className="mr-[15px]">
                          <input
                            type="radio"
                            id={radioId}
                            data-ui-id={radioId}
                            name={`ChoiceType|${field.formTemplateVersionFieldId}|${scoreTag}`}
                            value={option.fieldOptionId}
                            className="size-[22px] align-middle accent-brand"
                            checked={
                              selections[scoreTag]?.[field.fieldTypeId] === option.fieldOptionId
                            }
                            onChange={() =>
                              choose(scoreTag, field.fieldTypeId, option.fieldOptionId)
                            }
                          />
                          <label htmlFor={radioId} className="ml-1 align-middle">
                            {option.name}
                          </label>
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <Button
                uiId={`${pageId}-pane-${scoreTag}-actions-save`}
                variant="primary"
                size="sm"
                type="button"
                // C5-j：存檔真的呼叫 dataSource.update（endpoint-map 查無，見檔頭）。
                dataEffect={`update:${pageId}`}
                onClick={() => saveTab(scoreTag, score)}
              >
                儲存此頁籤設定
              </Button>
            </div>
          </div>
        ))}
      </ContentPanel>
    </div>
  );
}
